/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { lazy, Suspense, useEffect, useMemo, useState } from 'react';

import { CoordinationClient } from '@atlassiansox/engagekit-ts';
import { css, jsx } from '@compiled/react';
import { FormattedMessage, type MessageDescriptor, useIntl } from 'react-intl-next';

import { OPERATIONAL_EVENT_TYPE, SCREEN_EVENT_TYPE } from '@atlaskit/analytics-gas-types';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import {
	Spotlight,
	SpotlightManager,
	SpotlightTarget,
	SpotlightTransition,
} from '@atlaskit/onboarding';
import { N0 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';

import { type SupportedProducts } from '../../types';

// Lazy load animations component
const AnimationIllustration = lazy(() => import('../animation-illustration'));

import { messages } from './messages';

const SPOTLIGHT_MESSAGE_ID = {
	JIRA_SOFTWARE: 'joinable-product-switcher-jira',
	CONFLUENCE: 'joinable-product-switcher-confluence',
	JIRA_PRODUCT_DISCOVERY: 'joinable-product-switcher-jpd',
	JIRA_SERVICE_DESK: 'joinable-product-switcher-jsm',
};

const REMINDER_MESSAGE_ID = {
	JIRA_SOFTWARE: 'joinable-product-jira-reminder',
	CONFLUENCE: 'joinable-product-confluence-reminder',
	JIRA_PRODUCT_DISCOVERY: 'joinable-product-jpd-reminder',
	JIRA_SERVICE_DESK: 'joinable-product-jsm-reminder',
};

export type JoinableProductSpotlightType = {
	children: React.ReactNode;
	showSpotlight: boolean;
	onGetStartedItemClicked?: () => void;
	onDismissItemClicked?: () => void;
	cloudId: string;
	product: SupportedProducts;
	analyticsChannel?: string;
};

const productMessages: {
	[key in SupportedProducts]: { productName: string; description: MessageDescriptor };
} = {
	CONFLUENCE: {
		productName: 'Confluence',
		description: messages.confluenceDescription,
	},
	JIRA_SOFTWARE: {
		productName: 'Jira',
		description: messages.jiraDescription,
	},

	JIRA_PRODUCT_DISCOVERY: {
		productName: 'Jira Product Discovery',
		description: messages.jiraProductDiscoveryDescription,
	},
	JIRA_SERVICE_DESK: {
		productName: 'Jira Service Management',
		description: messages.jiraServiceManagementDescription,
	},
};

const ContainerStyles = css({
	height: '160px',
});
const LargeContainerStyles = css({
	height: '180px',
});

const HeadingDivStyles = css({
	paddingTop: token('space.150', '10px'),
	paddingRight: token('space.250', '20px'),
	paddingBottom: 0,
	paddingLeft: token('space.250', '20px'),
});

const AnimationContainerStyles = css({
	position: 'absolute',
	top: '0',
	left: '0',
	width: 'fit-content',
});

export const JoinableProductSpotlight = ({
	children,
	showSpotlight,
	onGetStartedItemClicked,
	onDismissItemClicked,
	cloudId,
	product,
	analyticsChannel,
}: JoinableProductSpotlightType) => {
	const { formatMessage } = useIntl();
	const [spotlightEligible, setSpotlightEligible] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [spotlightReason, setSpotlightReason] = useState<
		'sevenDayReminder' | 'firstTime' | undefined
	>();

	const coordinationClient = useMemo(() => {
		try {
			return new CoordinationClient({
				cloudId,
				stargateUrl: '/gateway/api',
			});
		} catch (_) {
			return null;
		}
	}, [cloudId]);

	useEffect(() => {
		const fireSpotlightScreenEvent = (spotlightReason: string) => {
			createAnalyticsEvent({
				eventType: SCREEN_EVENT_TYPE,
				name: 'joinProductSpotlight',
				attributes: {
					spotlightReason,
					product,
				},
			}).fire(analyticsChannel);
		};

		const fireNotEligibleOperational = (reason: string) => {
			createAnalyticsEvent({
				eventType: OPERATIONAL_EVENT_TYPE,
				action: 'failed',
				actionSubject: 'joinableProductSpotlightEligibility',
				attributes: {
					reason,
				},
			}).fire(analyticsChannel);
		};
		const checkSpotlightEligibility = async (coordinationClient: CoordinationClient) => {
			try {
				const startedProductCheck = await coordinationClient.start(SPOTLIGHT_MESSAGE_ID[product]);
				if (startedProductCheck) {
					setSpotlightReason('firstTime');
					setSpotlightEligible(startedProductCheck);
					fireSpotlightScreenEvent('firstTime');
					return;
				}

				const sevenDayReminder = await coordinationClient.start(REMINDER_MESSAGE_ID[product]);
				if (sevenDayReminder) {
					setSpotlightReason('sevenDayReminder');
					setSpotlightEligible(sevenDayReminder);
					fireSpotlightScreenEvent('reminder');
					return;
				}
				fireNotEligibleOperational('not-eligible');
			} catch (_) {
				fireNotEligibleOperational('error');
			}
		};
		if (coordinationClient && showSpotlight) {
			checkSpotlightEligibility(coordinationClient);
		}
	}, [coordinationClient, product, showSpotlight, createAnalyticsEvent, analyticsChannel]);

	const onDismissSpotlight = async () => {
		onDismissItemClicked?.();
		await coordinationClient?.stop(
			spotlightReason === 'firstTime'
				? SPOTLIGHT_MESSAGE_ID[product]
				: REMINDER_MESSAGE_ID[product],
		);
	};

	const onRequestOrJoinSite = async () => {
		onGetStartedItemClicked?.();
		await coordinationClient?.stop(
			spotlightReason === 'firstTime'
				? SPOTLIGHT_MESSAGE_ID[product]
				: REMINDER_MESSAGE_ID[product],
		);
	};

	const { productName, description } = productMessages[product];

	return (
		<SpotlightManager>
			<SpotlightTarget name="joinableProductSpotlight">{children}</SpotlightTarget>
			<SpotlightTransition>
				{showSpotlight && spotlightEligible && (
					<Spotlight
						testId="testId-joinable-product-in-switcher-spotlight"
						dialogPlacement={'right top'}
						dialogWidth={330}
						actions={[
							{
								onClick: onRequestOrJoinSite,
								text: formatMessage(messages.getStartedButtonText),
							},
							{
								onClick: onDismissSpotlight,
								text: formatMessage(messages.dismissButtonText),
							},
						]}
						header={({ children }) => (
							<div css={[product === 'CONFLUENCE' ? ContainerStyles : LargeContainerStyles]}>
								<div css={AnimationContainerStyles}>
									<Suspense fallback={null}>
										<AnimationIllustration product={product} />
									</Suspense>
									<div css={HeadingDivStyles}>{children}</div>
								</div>
							</div>
						)}
						heading={formatMessage(messages.spotlightTitleMessage, { product: productName })}
						target="joinableProductSpotlight"
						key="joinableProductSpotlight"
						targetRadius={3}
						targetBgColor={token('color.icon.inverse', N0)}
					>
						<FormattedMessage {...description} />
					</Spotlight>
				)}
			</SpotlightTransition>
		</SpotlightManager>
	);
};
